import { CoinsIcon, LockIcon, MessageCircleQuestionIcon, UsersIcon } from 'lucide-react'
import {
  Box,
  Grid,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import { AnalyticsBlock } from 'components/display/Analytics'
import { Card, Skeleton } from '@repo/ui'
import { EmptyState } from 'components'
import { useAskStats } from 'hooks/api'
import { useGlossaryCorpus } from 'docs/GlossaryProvider'
import { glossaryTerm } from 'docs/glossary'
import { formatNumber, relativeOrDateTime } from 'utils'
import { NANO_USD_PER_USD } from 'services/askStatsApi'
import type {
  AskStatsCompanyRow,
  AskStatsMetricCount,
  AskStatsQuestionCount,
  AskStatsSlugCount,
  AskStatsTermCount,
} from 'services/askStatsApi'

/**
 * The **Ask** tab of `/internal/usage` — cross-company Ask Vera figures for Vera staff
 * (VRG-227). Renders no `Header` of its own; `InternalUsagePage` owns the title and the
 * tab row.
 *
 * WAS `/internal/ask-stats`, a standalone route and its own sidebar row, on the
 * reasoning that its narrower permission could not survive being a tab. That reasoning
 * was sound about the PERMISSION and wrong about the conclusion: the permissions are
 * asymmetric then (`internal_support_manage` here vs `internal_analytics_view` for Tours),
 * and a route gated on EITHER carried that. Since VRG-685 (Spencer, 2026-10-08) both tabs
 * take `internal_analytics_view` and platform_manager does not see Analytics at all; see
 * `InternalUsagePage`'s docblock and `routes/internalAccess.ts`. The old path still resolves — it redirects here as
 * `?tab=ask`, because published guides link to it.
 *
 * Shape, permission handling, and the 403 empty state follow its sibling `ToursPanel`.
 *
 * Four sections, in VALUE order, not layout convenience — see each section's own
 * comment for why it sits where it does.
 */

const SectionNote = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
)

/**
 * The six little tables on this panel, with ONE row rhythm between them.
 *
 * MUI's `size="small"` pads cells `6px 16px`, which put every row at 31px — measurably
 * the tightest thing on a page whose cards pad 24px and whose sections sit 24px apart, so
 * the line items read as crammed against each other while everything around them breathes.
 * 10px takes a row to 39px, which is air without becoming the default `size` (16px
 * padding, ~55px rows) — far too heavy for a two-row table inside a card.
 *
 * A WRAPPER RATHER THAN AN sx ON EACH TABLE, and rather than touching all 17 `TableCell`s
 * individually: the padding is a property of "a table on this panel", so one component
 * owns it and the seventh table gets it by construction instead of by remembering. Sets
 * the rhythm on head and body cells alike, so the header keeps its relationship to the
 * rows it labels.
 */
const StatTable = ({ children }: { children: React.ReactNode }) => (
  <MuiTable
    size="small"
    sx={{ '& .MuiTableCell-root': { paddingTop: '10px', paddingBottom: '10px' } }}
  >
    {children}
  </MuiTable>
)

const HeadCell = ({
  children,
  align,
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) => (
  <TableCell
    align={align ?? 'left'}
    sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
  >
    {children}
  </TableCell>
)

const EmptyRow = ({ colSpan, text }: { colSpan: number; text: string }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ fontSize: 13, color: 'text.secondary' }}>
      {text}
    </TableCell>
  </TableRow>
)

// ─── Section 1 — Coverage failures ──────────────────────────────────────────

/**
 * The reason this page exists. `no_docs` grouped by question is the guides Vera owes;
 * `no_data` grouped by question is the fields Vera does not store. Different next
 * actions — "write a guide" versus "add a field" — so they never share a list.
 */
const QuestionCountTable = ({
  rows,
  emptyText,
}: {
  rows: AskStatsQuestionCount[]
  emptyText: string
}) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>Question</HeadCell>
        <HeadCell align="right">Asked</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={2} text={emptyText} />
      ) : (
        rows.map((row) => (
          <TableRow key={row.question}>
            <TableCell sx={{ fontSize: 13 }}>{row.question}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {row.count}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

// ─── Section 2 — What people ask about ──────────────────────────────────────

const MetricCountTable = ({ rows }: { rows: AskStatsMetricCount[] }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>Metric</HeadCell>
        <HeadCell align="right">Questions</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={2} text="No analytics metric has been queried yet." />
      ) : (
        rows.map((row) => (
          <TableRow key={row.metric}>
            <TableCell sx={{ fontSize: 13 }}>
              <code>{row.metric}</code>
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {row.count}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

const SlugCountTable = ({ rows }: { rows: AskStatsSlugCount[] }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>Guide</HeadCell>
        <HeadCell align="right">Cited</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={2} text="No guide has been cited yet." />
      ) : (
        rows.map((row) => (
          <TableRow key={row.slug}>
            <TableCell sx={{ fontSize: 13 }}>
              <code>{row.slug}</code>
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {row.count}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

/** Labels resolved against the STAFF glossary corpus (`useGlossaryCorpus` returns it
 * automatically under `/internal/*` — see GlossaryProvider) so an internal-only term
 * like `tag-family` still renders a real label rather than its bare id. */
const GlossaryTermCountTable = ({ rows }: { rows: AskStatsTermCount[] }) => {
  const corpus = useGlossaryCorpus()
  return (
    <StatTable>
      <TableHead>
        <TableRow>
          <HeadCell>Term</HeadCell>
          <HeadCell align="right">Used in</HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={2} text="No glossary term has appeared in an answer yet." />
        ) : (
          rows.map((row) => {
            const entry = glossaryTerm(row.termId, corpus)
            return (
              <TableRow key={row.termId}>
                <TableCell sx={{ fontSize: 13 }}>
                  {entry?.term ?? <code>{row.termId}</code>}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {row.count}
                </TableCell>
              </TableRow>
            )
          })
        )}
      </TableBody>
    </StatTable>
  )
}

// ─── Sections 3 & 4 — Adoption and cost per company ─────────────────────────
// Both read the SAME `perCompany` rows the service returns once — adoption cares about
// `questionCount`, cost cares about the token columns. Two small tables over one array
// rather than one wide table, so each section still reads as its own answer to its own
// question ("who's asking" vs "what does it cost").

const AdoptionCompanyTable = ({ rows }: { rows: AskStatsCompanyRow[] }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>Company</HeadCell>
        <HeadCell align="right">Questions</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={2} text="No company has asked a question yet." />
      ) : (
        rows.map((row) => (
          <TableRow key={row.companyId}>
            <TableCell sx={{ fontSize: 13 }}>{row.companyName ?? row.companyId}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {row.questionCount}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

/**
 * Nano-USD -> a dollar string.
 *
 * FOUR DECIMALS, not two. Real per-company spend here is fractions of a cent, so `$0.00`
 * would be the honest rounding of almost every row and the column would read as broken.
 * Above a dollar, cents are enough and four decimals is just noise.
 */
const formatNanoUsd = (nanoUsd: number): string => {
  const usd = nanoUsd / NANO_USD_PER_USD
  if (usd === 0) return '$0'
  if (usd < 1) return `$${usd.toFixed(4)}`
  return `$${usd.toFixed(2)}`
}

const CostCompanyTable = ({ rows }: { rows: AskStatsCompanyRow[] }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>Company</HeadCell>
        <HeadCell align="right">Input tokens</HeadCell>
        <HeadCell align="right">Output tokens</HeadCell>
        <HeadCell align="right">Tokens</HeadCell>
        {/* "Est." is doing real work — see the SectionNote above the table. This is
            list-price arithmetic over recorded tokens, not billed spend, and the header
            is the only place a scanner who never reads the note will see that. */}
        <HeadCell align="right">Est. cost</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={5} text="No token usage recorded yet." />
      ) : (
        rows.map((row) => (
          <TableRow key={row.companyId}>
            <TableCell sx={{ fontSize: 13 }}>{row.companyName ?? row.companyId}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {formatNumber(row.inputTokens)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {formatNumber(row.outputTokens)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {formatNumber(row.inputTokens + row.outputTokens)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
              {formatNanoUsd(row.estimatedCostNanoUsd)}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

// ─── Skeleton (§19 — mirrors the loaded layout, region for region) ─────────

const AskPanelSkeleton = () => (
  <Stack spacing={6}>
    <Grid container spacing={4}>
      {[0, 1, 2, 3].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Card sx={{ p: 6 }}>
            <Skeleton height={16} width="60%" />
            <Skeleton height={32} width="40%" />
          </Card>
        </Grid>
      ))}
    </Grid>
    {[0, 1, 2, 3].map((i) => (
      <Card key={i} title=" ">
        <Stack spacing={3} sx={{ mt: 4 }}>
          <Skeleton height={14} lines={3} />
        </Stack>
      </Card>
    ))}
  </Stack>
)

export const AskPanel = () => {
  const theme = useTheme()
  const { data, isLoading, error } = useAskStats()

  if (isLoading) return <AskPanelSkeleton />

  // A 403 is "you are not allowed to look", which must never render as "nobody has
  // asked a question yet" — distinct state, distinct copy (mirrors ToursPanel).
  //
  // Still reachable even though `InternalUsagePage` hides the Ask tab from anyone
  // lacking `internal_analytics_view` (VRG-685; was `internal_support_manage`): the tab filter is a client-side convenience and
  // the API is the real boundary (§27). A permission revoked mid-session, or the page
  // opened before the profile resolves, both land here.
  const status = (error as (Error & { status?: number }) | null)?.status
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
          title={status === 403 ? 'You cannot view Ask usage' : 'Could not load Ask usage'}
          text={
            status === 403
              ? 'Ask usage needs the "View analytics" permission. Ask a platform admin to grant it.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  if (!data) return null

  return (
    <>
      {/*
        `useFlexGap` IS LOAD-BEARING, NOT A STYLE PREFERENCE — without it every `Grid
        container` below paints 16px right of the plain `Card`s.

        MUI's default Stack (`useFlexGap={false}`) spaces children with margins, and to
        avoid double-spacing it first zeroes theirs. Its own source says so:

            // The useFlexGap={false} implement relies on each child to give up
            // control of the margin. We need to reset the margin to avoid double spacing.
            '& > :not(style):not(style)': { margin: 0 },

        But `Grid container spacing` cannot give up its margin — that is the entire
        mechanism by which it aligns. Legacy Grid pairs a container `margin-left: -16px`
        with an item `padding-left: 16px` so the two cancel. Stack kills the negative
        half and leaves the padding, so the cancellation never happens.

        Measured here before the fix: Grid rows painted at x=328 with `marginLeft: 0px`
        and `itemPaddingLeft: 16px`, while the Adoption and Cost cards — plain children,
        no Grid — painted at x=312. That 16px is the whole of the reported "top rows are
        shifted right".

        `useFlexGap` switches Stack to CSS `gap`, so it no longer touches child margins
        and Grid's own arithmetic survives. Same visual spacing, no reset.

        AND THIS IS THE ONLY SITE IN THE APP, which was worth establishing rather than
        assuming either way. A grep for "file contains both a spacing Stack and a Grid
        container" returned 20 candidates; all 19 others were audited and every one is a
        false positive. Three things have to be true at once for the pairing to break, and
        each eliminated some of them:

          - the Grid must have a TRUTHY `spacing` (several had none, so there is no
            negative margin to lose);
          - the Stack must have a TRUTHY `spacing` — `@mui/system/Stack/createStack.js:84`
            guards the whole block, reset included, behind `if (ownerState.spacing)`, so a
            bare `<Stack>` never touches its children's margins;
          - the Grid must be a DIRECT DOM child of that Stack (Fragments are transparent
            and do count; an intervening `Box`/`Card`/`TabPanel` does not).

        THE LOAD-BEARING REASON MOST PAGES ARE SAFE: `layouts/Layout/Layout.tsx:135` renders
        `<Outlet />` inside a plain `<Box sx={{ p: 6 }}>`. Many pages return a bare fragment
        at their root, so their Grid IS a direct child of whatever the layout provides — a
        Box today. If that ever becomes a `<Stack spacing>`, every page-root Grid in the app
        shifts by the spacing value at once, and this comment is the trail back.
      */}
      <Stack spacing={6} useFlexGap>
        <Typography variant="body3" color="text.secondary">
          All-time · computed {relativeOrDateTime(data.computedAt)}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsBlock
              title="Questions"
              icon={<MessageCircleQuestionIcon size={16} strokeWidth={1} />}
              stat={data.totalQuestions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsBlock
              title="Companies asking"
              icon={<UsersIcon size={16} strokeWidth={1} />}
              stat={data.adoption.totalCompanies}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsBlock
              title="Users asking"
              icon={<UsersIcon size={16} strokeWidth={1} />}
              stat={data.adoption.totalUsers}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsBlock
              title="Avg tokens / question"
              icon={<CoinsIcon size={16} strokeWidth={1} />}
              stat={Math.round(data.cost.avgTokensPerQuestion)}
            />
          </Grid>
        </Grid>

        {/* ── 1. Coverage failures — the reason this page exists ─────────────── */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card title="Guides we owe">
              <Stack spacing={3} sx={{ mt: 4 }}>
                <SectionNote>
                  Questions the model judged NO published guide covers, grouped by
                  question. This is a work queue — the answer is a new or corrected guide.
                </SectionNote>
                <QuestionCountTable
                  rows={data.coverageFailures.noDocs}
                  emptyText="Nothing uncovered yet."
                />
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card title="Data we don't have">
              <Stack spacing={3} sx={{ mt: 4 }}>
                <SectionNote>
                  Legitimate company-data questions Vera has no FIELD for, grouped by
                  question. No guide will ever answer these — the gap is in the data
                  model, not the docs.
                </SectionNote>
                <QuestionCountTable
                  rows={data.coverageFailures.noData}
                  emptyText="Nothing missing yet."
                />
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* ── 2. What people ask about ─────────────────────────────────────── */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card title="Metrics queried">
              <Box sx={{ mt: 4 }}>
                <MetricCountTable rows={data.whatPeopleAsk.metrics} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card title="Guides cited">
              <Box sx={{ mt: 4 }}>
                <SlugCountTable rows={data.whatPeopleAsk.citedDocs} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card title="Glossary terms used">
              <Box sx={{ mt: 4 }}>
                <GlossaryTermCountTable rows={data.whatPeopleAsk.glossaryTerms} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* ── 3. Adoption ──────────────────────────────────────────────────── */}
        <Card title="Adoption">
          <Stack spacing={3} sx={{ mt: 4 }}>
            <SectionNote>
              {data.adoption.totalCompanies === 0
                ? 'No company has asked a question yet.'
                : `${Math.round(data.adoption.repeatCompanyRate * 100)}% of companies that have asked a question have asked more than one.`}
            </SectionNote>
            <AdoptionCompanyTable rows={data.adoption.perCompany} />
          </Stack>
        </Card>

        {/* ── 4. Cost ──────────────────────────────────────────────────────── */}
        <Card title="Cost">
          <Stack spacing={3} sx={{ mt: 4 }}>
            <SectionNote>
              Tokens billed per company. {formatNumber(data.cost.monthlyPoolCeiling)} is the
              absolute ceiling any single company's monthly QUESTION pool can reach
              (the enterprise tier's unlimited-seats cap) — shown for scale, not as a
              per-company token budget, since the enforced pool is measured in questions,
              not tokens.
            </SectionNote>
            {/*
              THE ESTIMATE CAVEAT IS NOT OPTIONAL POLISH. This column is list-price
              arithmetic over recorded tokens, and it is going to be read as a bill unless
              it says otherwise. The API ships its own basis (`cost.pricing`) rather than
              the UI hardcoding rates, so this sentence cannot drift from the arithmetic
              that produced the numbers — if the rates or the assumed model change, this
              copy changes with them.

              `modelAttributionAvailable` is the one that matters most: `ask_query_log`
              has no `model` column, so every row is priced as the assumed model whether
              or not it answered. That is fine while one model serves every question and
              wrong the moment a second tier ships, so the disclosure is conditional on
              the flag rather than always-on — an always-on caveat would still be sitting
              there, ignored, on the day it stopped being true.
            */}
            <SectionNote>
              Estimated from published {data.cost.pricing.assumedModel} list pricing
              (${data.cost.pricing.inputUsdPerMillionTokens}/M in,{' '}
              ${data.cost.pricing.outputUsdPerMillionTokens}/M out, retrieved{' '}
              {data.cost.pricing.ratesRetrievedOn}) — not billed spend. Discounts, context
              caching and failed turns are not reflected.
              {!data.cost.pricing.modelAttributionAvailable && (
                <> Every question is priced as {data.cost.pricing.assumedModel}, because the
                query log does not record which model answered.</>
              )}{' '}
              Total {formatNanoUsd(data.cost.totalEstimatedCostNanoUsd)} across{' '}
              {formatNumber(data.totalQuestions)} questions.
            </SectionNote>
            <CostCompanyTable rows={data.cost.perCompany} />
          </Stack>
        </Card>
      </Stack>
    </>
  )
}
